import { useEffect, useState, useCallback } from "react";
import Icon from "@/components/Icon";
import { useAuth } from "@/context/AuthContext";
import CrmView from "@/pages/admin/CrmView";
import {
  getStats, getInbox, getLead, patchLead, replyLead, aiDraft, aiSummarize,
} from "@/lib/api";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "demo", label: "Demos" },
  { key: "contact", label: "Contact" },
  { key: "hot", label: "Hot" },
  { key: "important", label: "Important" },
  { key: "unread", label: "Unread" },
];
const PRIORITIES = ["low", "normal", "high", "urgent"];
const STATUSES = ["new", "open", "closed"];

function scoreClass(s) { return `adm-badge adm-badge--${(s || "warm")}`; }
function prioClass(p) { return `adm-prio adm-prio--${(p || "normal")}`; }
function fmt(ts) {
  if (!ts) return "";
  try { return new Date(ts).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return ts; }
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("inbox");
  const [stats, setStats] = useState({ total: 0, new: 0, hot: 0, important: 0 });
  const [filter, setFilter] = useState("all");
  const [list, setList] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState("");

  const loadList = useCallback(async (f) => {
    setLoadingList(true);
    try {
      const params = {};
      if (f === "demo" || f === "contact") params.type = f;
      else if (f !== "all") params.filter = f;
      const data = await getInbox(params);
      setList(data);
    } catch { setList([]); }
    finally { setLoadingList(false); }
  }, []);

  const loadStats = useCallback(async () => {
    try { setStats(await getStats()); } catch { /* ignore */ }
  }, []);

  useEffect(() => { document.title = "Secure Inbox | Proactive UMS"; }, []);
  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadList(filter); }, [filter, loadList]);

  const openLead = useCallback(async (id) => {
    setActiveId(id);
    setDetail(null);
    setReply("");
    try {
      const d = await getLead(id);
      setDetail(d);
      setList((prev) => prev.map((l) => (l.id === id ? { ...l, read: true } : l)));
      loadStats();
    } catch { /* ignore */ }
  }, [loadStats]);

  const refreshDetail = useCallback(async () => {
    if (!activeId) return;
    const d = await getLead(activeId);
    setDetail(d);
    setList((prev) => prev.map((l) => (l.id === activeId ? d.lead : l)));
  }, [activeId]);

  const doPatch = async (patch) => {
    if (!activeId) return;
    const updated = await patchLead(activeId, patch);
    setDetail((prev) => ({ ...prev, lead: updated }));
    setList((prev) => prev.map((l) => (l.id === activeId ? updated : l)));
    loadStats();
  };

  const send = async () => {
    if (!reply.trim() || !activeId) return;
    setBusy("send");
    try { await replyLead(activeId, reply.trim()); setReply(""); await refreshDetail(); loadStats(); }
    finally { setBusy(""); }
  };

  const draft = async () => {
    if (!activeId) return;
    setBusy("draft");
    try { const { draft } = await aiDraft(activeId); setReply(draft || ""); }
    finally { setBusy(""); }
  };

  const resummarize = async () => {
    if (!activeId) return;
    setBusy("ai");
    try { const lead = await aiSummarize(activeId); setDetail((p) => ({ ...p, lead })); setList((prev) => prev.map((l) => (l.id === activeId ? lead : l))); loadStats(); }
    finally { setBusy(""); }
  };

  const lead = detail?.lead;

  return (
    <div className="adm" data-testid="admin-dashboard">
      <header className="adm-top">
        <div className="adm-top__brand">
          <img src="/assets/logo-ums-header.png" alt="Proactive UMS+" />
          <span className="adm-top__tag">Secure Console</span>
        </div>
        <div className="adm-top__right">
          <div className="adm-user">
            {user?.picture ? <img src={user.picture} alt="" /> : <span className="adm-user__ph"><Icon name="User" size={16} /></span>}
            <div className="adm-user__meta"><strong>{user?.name || user?.email}</strong><small>{user?.role}</small></div>
          </div>
          <button className="adm-logout" onClick={logout} data-testid="admin-logout"><Icon name="LogOut" size={16} /> Sign out</button>
        </div>
      </header>

      <nav className="adm-tabs">
        <button className={`adm-tab ${tab === "inbox" ? "is-active" : ""}`} onClick={() => setTab("inbox")} data-testid="tab-inbox"><Icon name="Inbox" size={16} /> Secure Inbox</button>
        <button className={`adm-tab ${tab === "crm" ? "is-active" : ""}`} onClick={() => setTab("crm")} data-testid="tab-crm"><Icon name="Users" size={16} /> Mini CRM</button>
      </nav>

      {tab === "crm" && <CrmView />}

      {tab === "inbox" && (<>
      <div className="adm-stats">
        <div className="adm-stat" data-testid="stat-total"><span className="adm-stat__ic adm-stat__ic--blue"><Icon name="Inbox" size={18} /></span><div><strong>{stats.total}</strong><small>Total leads</small></div></div>
        <div className="adm-stat" data-testid="stat-new"><span className="adm-stat__ic adm-stat__ic--teal"><Icon name="Sparkles" size={18} /></span><div><strong>{stats.new}</strong><small>New / unhandled</small></div></div>
        <div className="adm-stat" data-testid="stat-hot"><span className="adm-stat__ic adm-stat__ic--red"><Icon name="Flame" size={18} /></span><div><strong>{stats.hot}</strong><small>Hot leads</small></div></div>
        <div className="adm-stat" data-testid="stat-important"><span className="adm-stat__ic adm-stat__ic--amber"><Icon name="Star" size={18} /></span><div><strong>{stats.important}</strong><small>Important</small></div></div>
      </div>

      <div className="adm-main">
        <aside className="adm-list">
          <div className="adm-filters">
            {FILTERS.map((f) => (
              <button key={f.key} className={`adm-chip ${filter === f.key ? "is-active" : ""}`} onClick={() => setFilter(f.key)} data-testid={`filter-${f.key}`}>{f.label}</button>
            ))}
          </div>
          <div className="adm-convos">
            {loadingList && <div className="adm-empty">Loading…</div>}
            {!loadingList && list.length === 0 && <div className="adm-empty">No conversations.</div>}
            {!loadingList && list.map((l) => (
              <button key={l.id} className={`adm-convo ${activeId === l.id ? "is-active" : ""} ${!l.read ? "is-unread" : ""}`} onClick={() => openLead(l.id)} data-testid={`convo-${l.id}`}>
                <div className="adm-convo__row1">
                  <span className="adm-convo__name">{l.important && <Icon name="Star" size={13} className="adm-star-on" />}{l.name}</span>
                  <span className="adm-convo__time">{fmt(l.created_at)}</span>
                </div>
                <div className="adm-convo__row2">
                  <span className={`adm-type adm-type--${l.type}`}>{l.type}</span>
                  {l.lead_score && <span className={scoreClass(l.lead_score)}>{l.lead_score}</span>}
                  <span className="adm-convo__org">{l.organization || l.email}</span>
                </div>
                <p className="adm-convo__snip">{l.ai_summary || l.message}</p>
              </button>
            ))}
          </div>
        </aside>

        <section className="adm-detail">
          {!lead && <div className="adm-detail__empty"><Icon name="MessageSquare" size={40} /><p>Select a conversation to view AI insights and reply.</p></div>}
          {lead && (
            <>
              <div className="adm-detail__head">
                <div>
                  <h2>{lead.name}</h2>
                  <div className="adm-detail__contact">
                    <a href={`mailto:${lead.email}`}><Icon name="Mail" size={14} /> {lead.email}</a>
                    {lead.phone && <span><Icon name="Phone" size={14} /> {lead.phone}</span>}
                    {lead.organization && <span><Icon name="Building2" size={14} /> {lead.organization}</span>}
                    {lead.interest && <span><Icon name="Tag" size={14} /> {lead.interest}</span>}
                  </div>
                </div>
                <div className="adm-detail__controls">
                  <button className={`adm-icontog ${lead.important ? "is-on" : ""}`} title="Mark important" onClick={() => doPatch({ important: !lead.important })} data-testid="toggle-important"><Icon name="Star" size={16} /></button>
                  <select className="adm-select" value={lead.priority || "normal"} onChange={(e) => doPatch({ priority: e.target.value })} data-testid="select-priority">
                    {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                  <select className="adm-select" value={lead.status || "new"} onChange={(e) => doPatch({ status: e.target.value })} data-testid="select-status">
                    {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>

              <div className="adm-ai" data-testid="ai-panel">
                <div className="adm-ai__head">
                  <span className="adm-ai__title"><Icon name="Brain" size={15} /> AI Lead Intelligence</span>
                  <button className="adm-ai__btn" onClick={resummarize} disabled={busy === "ai"} data-testid="ai-resummarize">{busy === "ai" ? "Analyzing…" : "Re-analyze"}</button>
                </div>
                <div className="adm-ai__badges">
                  <span className={scoreClass(lead.lead_score)}>{lead.lead_score || "unscored"}</span>
                  <span className={prioClass(lead.priority)}>priority: {lead.priority || "normal"}</span>
                </div>
                <p className="adm-ai__summary">{lead.ai_summary || "No summary yet."}</p>
                {lead.lead_reason && <p className="adm-ai__reason"><Icon name="Info" size={13} /> {lead.lead_reason}</p>}
              </div>

              <div className="adm-thread" data-testid="thread">
                {(detail.thread || []).map((m) => (
                  <div key={m.id} className={`adm-msg adm-msg--${m.direction === "out" ? "out" : "in"}`}>
                    <div className="adm-msg__meta"><strong>{m.author_name || m.sender}</strong> · {fmt(m.created_at)} · <em>{m.channel}</em></div>
                    <div className="adm-msg__bubble">{m.text}</div>
                  </div>
                ))}
              </div>

              <div className="adm-composer">
                <textarea className="adm-composer__input" placeholder="Write a reply…" value={reply} onChange={(e) => setReply(e.target.value)} data-testid="reply-input" />
                <div className="adm-composer__actions">
                  <button className="adm-btn adm-btn--ghost" onClick={draft} disabled={busy === "draft"} data-testid="ai-draft-btn"><Icon name="Sparkles" size={15} /> {busy === "draft" ? "Drafting…" : "AI Draft"}</button>
                  <button className="adm-btn adm-btn--blue" onClick={send} disabled={busy === "send" || !reply.trim()} data-testid="send-reply-btn"><Icon name="Send" size={15} /> {busy === "send" ? "Sending…" : "Send"}</button>
                </div>
              </div>
            </>
          )}
        </section>
      </div>
      </>)}
    </div>
  );
}
